import { FC, HTMLAttributes } from "react"
import { useLocalizedDateFormat } from "@/utils/dateUtils"

interface DateDisplayProps extends HTMLAttributes<HTMLSpanElement> {
  date?: string | Date | null
  format?: "createdAt" | "lastLogin" | "table" | "tableDateTime" | "full" | "relative"
  showRelative?: boolean
  tooltip?: boolean
}

/**
 * Reusable component for displaying dates with user's preferences
 * Automatically formats dates based on the browser timezone and language settings
 */
export const DateDisplay: FC<DateDisplayProps> = ({
  date,
  format = "tableDateTime",
  showRelative = false,
  tooltip = true,
  className,
  children,
  ...props
}) => {
  const {
    formatCreatedAt,
    formatLastLogin,
    formatTableDate,
    formatTableDateTime,
    formatFullDateTime,
    formatRelativeTime,
  } = useLocalizedDateFormat()

  const formatDateFn = () => {
    // Handle null, undefined, or empty dates
    if (!date) {
      return "Never"
    }

    const dateStr = typeof date === "string" ? date : date.toISOString()

    switch (format) {
      case "createdAt":
        return formatCreatedAt(dateStr)
      case "lastLogin":
        return formatLastLogin(dateStr)
      case "table":
        return formatTableDate(dateStr)
      case "tableDateTime":
        return formatTableDateTime(dateStr)
      case "full":
        return formatFullDateTime(dateStr)
      case "relative":
        return formatRelativeTime(dateStr)
      default:
        return formatTableDateTime(dateStr)
    }
  }

  const formattedDate = formatDateFn()
  const tooltipContent = tooltip && date ? formatFullDateTime(date as string) : undefined

  return (
    <span className={className} title={tooltipContent} {...props}>
      {children || formattedDate}
      {showRelative && format !== "relative" && date && (
        <span className="text-muted-foreground/60 ml-1">
          ({formatRelativeTime(date as string)})
        </span>
      )}
    </span>
  )
}

/**
 * Component specifically for table dates
 */
export const TableDateDisplay: FC<Omit<DateDisplayProps, "format">> = (props) => {
  return <DateDisplay {...props} format="tableDateTime" showRelative={true} />
}

/**
 * Component specifically for creation dates
 */
export const CreatedAtDisplay: FC<Omit<DateDisplayProps, "format">> = (props) => {
  return <DateDisplay {...props} format="createdAt" showRelative={true} />
}

/**
 * Component specifically for last login dates
 */
export const LastLoginDisplay: FC<Omit<DateDisplayProps, "format">> = (props) => {
  return <DateDisplay {...props} format="lastLogin" showRelative={true} />
}
